import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Chip,
  TablePagination,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import TablePaginationActions from "../../../globals/components/TablePaginationActions";
import BondListTable from "../../components/BondListTable";

/** Shared law-firm bonds table chrome (Phase 9.14B). */
function LawFirmBondsTableBase({
  isTeamMember,
  pageConfig,
  loading,
  pendingCount,
  noPendingBonds,
  onPendingApprovalChip,
  onRefresh,
  onOpenFilters,
  tableColumns,
  tableColSpan,
  filteredRows,
  orderBy,
  order,
  onSort,
  onViewBond,
  onOpenCancel,
  onOpenEndorse,
  capitalizeFirst,
  filteredDataLength,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onCantFindBond,
}) {
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        {isTeamMember ? (
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Bonds to Approve
            </Typography>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Here are all the bonds that require your approval.
            </span>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "60vw" }}>
            {pageConfig.headerIcon === "plain" ? (
              <Typography variant="h4" fontWeight="bold">
                {pageConfig.title}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {pageConfig.headerIcon === "cancel" && (
                  <CancelIcon sx={{ color: "error.main" }} />
                )}
                {pageConfig.headerIcon === "endorse" && (
                  <EditIcon sx={{ color: "#6b21a8" }} />
                )}
                <Typography variant="h4" fontWeight="bold">
                  {pageConfig.title}
                </Typography>
              </Box>
            )}
            <span style={{ fontSize: "14px", color: "#666" }}>{pageConfig.subtitle}</span>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2, alignItems: isTeamMember ? "center" : undefined }}>
          {isTeamMember && (
            <Chip
              icon={<PendingActionsIcon />}
              label={`Pending (${pendingCount})`}
              color="primary"
              variant="outlined"
              onClick={() => onPendingApprovalChip()}
              disabled={noPendingBonds}
            />
          )}
          <Button
            onClick={() => onRefresh()}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
          <Button
            variant="contained"
            onClick={() => onOpenFilters()}
            startIcon={<FilterListIcon />}
          >
            Filters
          </Button>
        </Box>
      </Box>

      <BondListTable
        tableColumns={tableColumns}
        tableColSpan={tableColSpan}
        loading={loading}
        filteredRows={filteredRows}
        orderBy={orderBy}
        order={order}
        onSort={onSort}
        isTeamMember={isTeamMember}
        showRowCancel={pageConfig.showRowCancel}
        showRowEndorse={pageConfig.showRowEndorse}
        onViewBond={onViewBond}
        onOpenCancel={onOpenCancel}
        onOpenEndorse={onOpenEndorse}
        capitalizeFirst={capitalizeFirst}
        footer={
          isTeamMember ? (
            <TablePagination
              ActionsComponent={TablePaginationActions}
              component="div"
              count={filteredDataLength}
              page={page}
              onPageChange={(e, p) => onPageChange(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => onRowsPerPageChange(Number.parseInt(e.target.value, 10))}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 2 }}
            />
          ) : (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                variant="body2"
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => onCantFindBond()}
              >
                Can't find a bond?
              </Typography>
              <TablePagination
                ActionsComponent={TablePaginationActions}
                component="div"
                count={filteredDataLength}
                page={page}
                onPageChange={(e, p) => onPageChange(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => onRowsPerPageChange(Number.parseInt(e.target.value, 10))}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ m: 0 }}
              />
            </Box>
          )
        }
      />
    </>
  );
}

LawFirmBondsTableBase.propTypes = {
  isTeamMember: PropTypes.bool.isRequired,
  pageConfig: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  pendingCount: PropTypes.number,
  noPendingBonds: PropTypes.bool,
  onPendingApprovalChip: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onOpenFilters: PropTypes.func.isRequired,
  tableColumns: PropTypes.array.isRequired,
  tableColSpan: PropTypes.number.isRequired,
  filteredRows: PropTypes.array.isRequired,
  orderBy: PropTypes.string,
  order: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onViewBond: PropTypes.func.isRequired,
  onOpenCancel: PropTypes.func.isRequired,
  onOpenEndorse: PropTypes.func.isRequired,
  capitalizeFirst: PropTypes.func.isRequired,
  filteredDataLength: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onCantFindBond: PropTypes.func,
};

export default LawFirmBondsTableBase;
